import React from 'react';
import { motion } from 'framer-motion';
import { Settings, Plus, Check, Info } from 'lucide-react';
import { behaviorCategories } from '../../data/journalBehaviors';
import { useJournalStore } from '../../store/journalStore';

export function BehaviorManager() {
  const { selectedBehaviors, addBehavior, removeBehavior } = useJournalStore();

  const handleBehaviorToggle = (categoryId: string, behaviorId: string, behaviorName: string, unit?: string) => {
    const isSelected = selectedBehaviors.some(b => b.id === behaviorId);
    
    if (isSelected) {
      removeBehavior(behaviorId);
    } else {
      addBehavior({
        id: behaviorId,
        name: behaviorName,
        category: categoryId,
        unit
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="theme-card p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Settings className="w-5 h-5 text-primary" />
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
            </div>
            <h2 className="text-lg font-semibold">Customize Your Journal</h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Info className="w-4 h-4" />
            <span>Select behaviors to track daily</span>
          </div>
        </div>
        
        <div className="space-y-8">
          {behaviorCategories.map((category) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <h3 className="text-base font-medium flex items-center gap-2">
                {category.name}
                <span className="text-xs text-muted-foreground">
                  ({selectedBehaviors.filter(b => b.category === category.id).length} selected)
                </span>
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {category.behaviors.map((behavior) => {
                  const isSelected = selectedBehaviors.some(b => b.id === behavior.id);
                  
                  return (
                    <button
                      key={behavior.id}
                      onClick={() => handleBehaviorToggle(category.id, behavior.id, behavior.name, behavior.unit)}
                      className={`p-4 rounded-lg text-left relative group overflow-hidden ${
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted/70'
                      }`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="relative">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{behavior.name}</div>
                            {behavior.unit && (
                              <div className="text-xs opacity-80">Unit: {behavior.unit}</div>
                            )}
                          </div>
                          {isSelected ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="bg-muted/30 p-4 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Info className="w-4 h-4" />
            <p>Selected behaviors will appear in your daily journal for tracking. You can update these anytime.</p>
          </div>
        </div>
      </div>
    </div>
  );
}